import React, {
  createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import usePinLock from '../hooks/usePinLock';

// Two-stage inactivity timeout (item 106), measured from the last user activity:
//  - 3 min idle  → PIN lock (session stays alive; a PIN unlocks it, no re-login)
//  - 30 min idle → full Auth0 logout (the security backstop)
// Fixed for now (the Settings card shows them read-only).
export const PIN_LOCK_IDLE_MS = 3 * 60 * 1000;
export const PIN_LOGOUT_IDLE_MS = 30 * 60 * 1000;

// activity that resets the idle timers (passive listeners; only while unlocked)
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];

interface PinLockContextValue {
  enabled: boolean;
  locked: boolean;
  unlock: () => void;
}

const PinLockContext = createContext<PinLockContextValue>({
  enabled: false,
  locked: false,
  unlock: () => {},
});

export const PinLockProvider = ({ children }: { children: ReactNode }) => {
  const { logout } = useAuth0();
  const { pinLock } = usePinLock();
  // the lock is only live once the user enabled it AND has a PIN set
  const enabled = Boolean(pinLock?.enabled && pinLock?.isSet);

  const [locked, setLocked] = useState(false);
  const lockedRef = useRef(false);
  lockedRef.current = locked;

  const lockTimer = useRef<number>();
  const logoutTimer = useRef<number>();

  const doLogout = useCallback(() => {
    logout({ returnTo: window.location.origin });
  }, [logout]);

  const clearTimers = useCallback(() => {
    window.clearTimeout(lockTimer.current);
    window.clearTimeout(logoutTimer.current);
  }, []);

  const armTimers = useCallback(() => {
    clearTimers();
    lockTimer.current = window.setTimeout(() => setLocked(true), PIN_LOCK_IDLE_MS);
    logoutTimer.current = window.setTimeout(doLogout, PIN_LOGOUT_IDLE_MS);
  }, [clearTimers, doLogout]);

  const unlock = useCallback(() => setLocked(false), []);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return undefined;
    }
    // Once locked, leave the logout timer running (the 30-min backstop keeps
    // counting behind the lock screen) and stop listening for activity so stray
    // events don't reset it. Re-arm both fresh when we unlock.
    if (locked) return undefined;

    armTimers();
    const onActivity = () => { if (!lockedRef.current) armTimers(); };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [enabled, locked, armTimers, clearTimers]);

  // belt-and-suspenders cleanup on unmount
  useEffect(() => clearTimers, [clearTimers]);

  return (
    <PinLockContext.Provider value={{ enabled, locked, unlock }}>
      {children}
    </PinLockContext.Provider>
  );
};

export const usePinLockState = (): PinLockContextValue => useContext(PinLockContext);

export default PinLockProvider;
